import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiFileText, FiPlus, FiSearch } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { IfCanWrite } from '../utilities/adminAccess';
import { entityFiltersToQuery, emptyEntityFilters, searchTypeLabelKey } from '../utilities/adminSearch';
import {
  NEWS_PAGE_SIZE,
  extractNewsList,
  formatNewsDate,
  getNewsImageUrl,
  isNewsEnabled,
  newsCardPath,
  newsEntityKey,
  newsImageOnError,
  newsNewPath,
  newsTagKeys,
  parseNewsEntities,
} from '../utilities/newsApi';
import NewsEntityFilters from '../components/NewsEntityFilters';
import NewsTagPicker from '../components/NewsTagPicker';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/usersManagement.css';
import '../styles/newsManagement.css';

export default function NewsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tags, setTags] = useState([]);
  const [entityFilters, setEntityFilters] = useState(emptyEntityFilters);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadList(searchQuery, tags, entityFilters, 0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, tags, entityFilters]);

  const loadList = async (search, selectedTags, filters, offset, append) => {
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = {
        limit: NEWS_PAGE_SIZE,
        offset,
        sort: 'date',
        ...entityFiltersToQuery(filters),
      };
      if (search && search.trim()) body.search = search.trim();
      const tagKeys = newsTagKeys(selectedTags);
      if (tagKeys.length) body.tags = tagKeys;

      const response = await authorizedPost(dispatch, API_URLS.getAdminNewsList, body);
      if (requestId !== requestIdRef.current) return;

      const page = extractNewsList(response.data);
      const nextItems = append ? itemsRef.current.concat(page.items) : page.items;
      setItems(nextItems);
      setTotal(page.total);
      setHasMore(
        page.total != null
          ? nextItems.length < page.total
          : page.items.length === NEWS_PAGE_SIZE
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setItems([]);
        setTotal(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('newsLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const columns = [
    {
      key: 'title',
      label: t('newsTitle'),
      className: 'st-cell-name',
      render: (item) => {
        const photo = getNewsImageUrl(item.image, item.updated_at || item.created_at || item.id);
        return (
          <span className="news-title-cell">
            <span className={`news-thumb ${photo ? 'has-image' : ''}`}>
              {photo ? (
                <img
                  src={photo}
                  alt=""
                  onError={newsImageOnError}
                />
              ) : <FiFileText />}
            </span>
            <span className="news-title-text">
              <strong>{item.title || '—'}</strong>
              {item.preview ? <small>{item.preview}</small> : null}
            </span>
          </span>
        );
      },
    },
    {
      key: 'tags',
      label: t('newsTags'),
      className: 'usr-cell-roles hide-sm',
      sortable: false,
      render: (item) => {
        const keys = newsTagKeys(item.tags);
        if (!keys.length) return <span className="news-muted">{t('newsNoTags')}</span>;
        return (
          <span className="usr-chips">
            {keys.map((key) => (
              <span key={key} className="usr-chip">{key}</span>
            ))}
          </span>
        );
      },
    },
    {
      key: 'entities',
      label: t('newsEntities'),
      className: 'usr-cell-roles news-cell-entities hide-sm',
      sortable: false,
      render: (item) => {
        const entities = parseNewsEntities(item.entities, { fromServer: true });
        if (!entities.length) return <span className="news-muted">{t('newsNoEntities')}</span>;
        return (
          <span className="usr-chips news-entity-chips">
            {entities.map((entity) => {
              const typeKey = searchTypeLabelKey(entity.type);
              return (
                <span key={newsEntityKey(entity)} className="usr-chip news-entity-chip">
                  <span className="news-tag-chip-text">
                    <span>{entity.name || `#${entity.id}`}</span>
                    <small>{typeKey ? t(typeKey) : entity.type}</small>
                  </span>
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      label: t('newsDate'),
      className: 'news-cell-date',
      render: (item) => formatNewsDate(item.created_at),
    },
    {
      key: 'enabled',
      label: t('stStatus'),
      className: 'news-cell-status',
      sortable: false,
      render: (item) => (
        <span className={`news-status ${isNewsEnabled(item.enabled) ? 'is-on' : 'is-off'}`}>
          {isNewsEnabled(item.enabled) ? t('stEnabled') : t('stDisabled')}
        </span>
      ),
    },
  ];

  const foundCount = total != null ? total : items.length;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page news-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navNews') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="news-toolbar">
            <div className="news-toolbar-searches">
              <div className="st-search">
                <FiSearch className="st-search-icon" />
                <input
                  type="text"
                  className="st-search-input"
                  placeholder={t('newsSearchPh')}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
                {searchQuery ? (
                  <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                    ×
                  </button>
                ) : null}
              </div>
              <div className="news-filter-tags">
                <NewsTagPicker value={tags} onChange={setTags} allowCreate={false} />
              </div>
            </div>
            <NewsEntityFilters value={entityFilters} onChange={setEntityFilters} />
            <div className="st-meta">
              <strong>{t('newsFound', { count: foundCount })}</strong>
            </div>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navNews')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('newsLoading')}</p>
                </div>
              ) : (
                <>
                  <ReorderableTable
                    tableClassName="st-table-news"
                    columns={columns}
                    items={items}
                    getRowKey={(item, index) => item.id || index}
                    reorderMode={false}
                    getRowHref={(item) => (item.id ? newsCardPath(item.id) : null)}
                    empty={(
                      <div className="st-empty">
                        <FiFileText size={48} />
                        <h3>{t('newsEmpty')}</h3>
                        <p>{t('newsEmptyHint')}</p>
                      </div>
                    )}
                  />
                  {hasMore ? (
                    <div className="usr-load-more">
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loadingMore}
                        onClick={() => loadList(searchQuery, tags, entityFilters, items.length, true)}
                      >
                        {loadingMore ? t('newsLoading') : t('dqLoadMore')}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="st-actions">
          <IfCanWrite entity="news">
            <Link
              to={newsNewPath()}
              className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
              aria-disabled={loading || undefined}
              onClick={(event) => { if (loading) event.preventDefault(); }}
            >
              <FiPlus /> {t('newsCreate')}
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
